// Considered: DialogWrapper — rejected because the design is a right-side drawer
// with search/filter/by-org bars/row list, not a centered modal; built on DrawerAtom.
// See spec Reuse Audit.
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'localization';
import DrawerAtom from '../../atoms/Drawer/DrawerAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import SubscriberRowMolecule from '../../molecules/marketplace/SubscriberRowMolecule';
import type { AudienceSummary } from 'types/channel';
import './SubscribersDrawerOrganism.scss';

export interface SubscribersDrawerOrganismProps {
  visible: boolean;
  onHide: () => void;
  audience: AudienceSummary;
  channelId: string;
}

type TypeFilter = 'all' | 'spheres' | 'people';

const NS = 'marketplace.channelManage.v3Audience';

const SubscribersDrawerOrganism: React.FC<SubscribersDrawerOrganismProps> = ({
  visible,
  onHide,
  audience,
  channelId: _channelId,
}) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  // Reset search and filter when drawer opens
  useEffect(() => {
    if (visible) {
      setQ('');
      setTypeFilter('all');
    }
  }, [visible]);

  const filteredRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return audience.sample.filter((row) => {
      if (typeFilter === 'spheres' && row.kind !== 'sphere') return false;
      if (typeFilter === 'people' && row.kind !== 'person') return false;
      if (term) {
        const hay = `${row.name} ${row.org} ${row.type}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [audience.sample, q, typeFilter]);

  const filterOptions = [
    { value: 'all' as TypeFilter, label: t(`${NS}.drawer.filter.all`) },
    { value: 'spheres' as TypeFilter, label: t(`${NS}.drawer.filter.spheres`) },
    { value: 'people' as TypeFilter, label: t(`${NS}.drawer.filter.people`) },
  ];

  const maxOrgCount = Math.max(...audience.byOrg.map((o) => o.count), 1);

  const header = (
    <div className="subscribers-drawer-organism__header">
      <span className="subscribers-drawer-organism__title">
        {t(`${NS}.drawer.title`)}
      </span>
      {audience.restrictedCount > 0 && (
        <StatusBadgeAtom
          tone="warn"
          icon="pi pi-lock"
          label={t(`${NS}.breakdown.privateSpheres`, { count: audience.restrictedCount })}
          className="subscribers-drawer-organism__hidden-badge"
        />
      )}
    </div>
  );

  return (
    <DrawerAtom
      visible={visible}
      onHide={onHide}
      position="right"
      width="480px"
      header={header}
      className="subscribers-drawer-organism"
    >
      <div className="subscribers-drawer-organism__body">
        <div className="subscribers-drawer-organism__search">
          <i className="pi pi-search subscribers-drawer-organism__search-icon" aria-hidden="true" />
          <input
            type="search"
            className="subscribers-drawer-organism__search-input"
            placeholder={t(`${NS}.drawer.searchPlaceholder`)}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t(`${NS}.drawer.searchPlaceholder`)}
          />
        </div>

        <SegmentedControlAtom
          options={filterOptions}
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as TypeFilter)}
          mode="single"
          className="subscribers-drawer-organism__filter"
          ariaLabel={t(`${NS}.drawer.filter.all`)}
        />

        {audience.byOrg.length > 0 && (
          <div className="subscribers-drawer-organism__by-org">
            <span className="subscribers-drawer-organism__by-org-title">
              {t(`${NS}.drawer.byOrg`)}
            </span>
            <ul className="subscribers-drawer-organism__org-list" aria-label={t(`${NS}.drawer.byOrg`)}>
              {audience.byOrg.map((org) => (
                <li key={org.organizationId} className="subscribers-drawer-organism__org-bar">
                  <span className="subscribers-drawer-organism__org-name">{org.org}</span>
                  <div className="subscribers-drawer-organism__org-bar-track">
                    <div
                      className="subscribers-drawer-organism__org-bar-fill"
                      style={{ width: `${(org.count / maxOrgCount) * 100}%` }}
                    />
                  </div>
                  <span className="subscribers-drawer-organism__org-count">{org.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul
          className="subscribers-drawer-organism__rows"
          aria-label={t(`${NS}.drawer.title`)}
        >
          {filteredRows.map((row) => (
            <li key={row.id} className="subscribers-drawer-organism__row">
              <SubscriberRowMolecule row={row} />
            </li>
          ))}
          {filteredRows.length === 0 && (
            <li className="subscribers-drawer-organism__empty">
              {t(`${NS}.drawer.noResults`)}
            </li>
          )}
        </ul>
      </div>
    </DrawerAtom>
  );
};

SubscribersDrawerOrganism.displayName = 'SubscribersDrawerOrganism';
export default SubscribersDrawerOrganism;
